// Copyright (C) 2026 Aidian Holder
// SPDX-License-Identifier: GPL-3.0-only

import maplibregl from 'maplibre-gl';
import makiLayout from 'maki/layouts/all.json';
import { FONTS, loadFont } from './locator-panel.js';

// Use 15px icons by default
const ICON_SIZE = 15;

export class IconsPanel {
  constructor(map) {
    this._map = map;
    this._icons    = [];   // { marker, el, iconName }
    this._iconSize = ICON_SIZE;
    this._activeEl = null;
  }

  mount(container) {
    this._panel = document.createElement('div');
    this._panel.id = 'icons-panel';

    const header = document.createElement('div');
    header.className = 'ip-header';

    const title = document.createElement('h2');
    title.textContent = 'Icons';

    const collapseBtn = document.createElement('button');
    collapseBtn.className = 'ip-collapse-btn';
    collapseBtn.type = 'button';
    collapseBtn.textContent = '✕';
    collapseBtn.title = 'Collapse panel';
    collapseBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      container.dispatchEvent(new CustomEvent('panel-close', { bubbles: true }));
    });

    header.appendChild(title);
    header.appendChild(collapseBtn);

    // ── Size slider ──────────────────────────────────────────────────────────
    const sizeRow = document.createElement('div');
    sizeRow.className = 'ip-size-row';

    const sizeLabel = document.createElement('label');
    sizeLabel.textContent = 'Size';
    sizeLabel.className = 'ip-size-label';

    const slider = document.createElement('input');
    slider.type  = 'range';
    slider.min   = 10;
    slider.max   = 60;
    slider.step  = 1;
    slider.value = this._iconSize;
    slider.className = 'ip-size-slider';

    const sizeVal = document.createElement('span');
    sizeVal.className = 'ip-size-val';
    sizeVal.textContent = `${this._iconSize}px`;

    this._sizeSlider = slider;
    this._sizeValEl  = sizeVal;

    slider.addEventListener('input', () => {
      this._iconSize = Number(slider.value);
      sizeVal.textContent = `${this._iconSize}px`;
      if (this._activeEl) this._resizeIcon(this._activeEl, this._iconSize);
    });

    sizeRow.append(sizeLabel, slider, sizeVal);

    const grid = document.createElement('div');
    grid.className = 'ip-grid';

    makiLayout.all.forEach(iconName => {
      const cell = document.createElement('div');
      cell.className = 'ip-cell';
      cell.title = `Add ${iconName} icon`;

      // Use the raw SVG from maki icons
      const img = document.createElement('img');
      img.src = `/icons/${iconName}-${ICON_SIZE}.svg`;
      img.className = 'ip-thumb';
      cell.appendChild(img);

      cell.addEventListener('click', () => this._addIcon(iconName));
      grid.appendChild(cell);
    });

    this._panel.appendChild(header);
    this._panel.appendChild(sizeRow);
    this._panel.appendChild(grid);
    container.appendChild(this._panel);

    // ── Infowindow Editor (Bottom Overlay) ───────────────────────────────────
    this._iwPanel = document.createElement('div');
    this._iwPanel.id = 'iw-editor';
    this._iwPanel.style.display = 'none';

    const iwHeader = document.createElement('div');
    iwHeader.className = 'iw-editor-header';
    iwHeader.textContent = 'Infowindow';
    this._iwPanel.appendChild(iwHeader);

    const fields = document.createElement('div');
    fields.className = 'iw-editor-fields';

    const makeField = (label, tag = 'input') => {
      const row = document.createElement('div');
      row.className = 'iw-editor-row';
      const lbl = document.createElement('label');
      lbl.textContent = label;
      const input = document.createElement(tag);
      if (tag === 'input') input.type = 'text';
      input.addEventListener('click', e => e.stopPropagation());
      row.append(lbl, input);
      fields.appendChild(row);
      return input;
    };

    this._iwTitle   = makeField('Title');
    this._iwSubhead = makeField('Subhead', 'textarea');

    // ── Rich-text "Text" field ────────────────────────────────────────────
    const textRow = document.createElement('div');
    textRow.className = 'iw-editor-row';
    const textLabel = document.createElement('label');
    textLabel.textContent = 'Text';

    const textWrap = document.createElement('div');
    textWrap.style.flex = '1';
    textWrap.style.display = 'flex';
    textWrap.style.flexDirection = 'column';
    textWrap.style.gap = '3px';

    // Toolbar
    const toolbar = document.createElement('div');
    toolbar.className = 'iw-toolbar';

    const makeFmtBtn = (html, title, cmd) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.innerHTML = html;
      btn.title = title;
      btn.className = 'iw-tool-btn';
      btn.addEventListener('mousedown', e => e.preventDefault()); // keep focus
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        document.execCommand(cmd, false, null);
        richEditor.focus();
      });
      return btn;
    };

    const toolSep = () => {
      const s = document.createElement('span');
      s.className = 'iw-toolbar-sep';
      return s;
    };

    toolbar.appendChild(makeFmtBtn('<b>B</b>', 'Bold',      'bold'));
    toolbar.appendChild(makeFmtBtn('<i>I</i>', 'Italic',    'italic'));
    toolbar.appendChild(makeFmtBtn('<u>U</u>', 'Underline', 'underline'));
    toolbar.appendChild(toolSep());
    toolbar.appendChild(makeFmtBtn('&#8226;&#8212;', 'Unordered list', 'insertUnorderedList'));
    toolbar.appendChild(makeFmtBtn('1&#8212;',       'Ordered list',   'insertOrderedList'));
    toolbar.appendChild(toolSep());

    // Link button — saves selection before dialog opens
    let _savedRange = null;
    const linkBtn = document.createElement('button');
    linkBtn.type = 'button';
    linkBtn.textContent = '🔗';
    linkBtn.title = 'Insert link';
    linkBtn.className = 'iw-tool-btn';
    linkBtn.addEventListener('mousedown', (e) => {
      e.preventDefault();
      const sel = window.getSelection();
      if (sel && sel.rangeCount > 0) _savedRange = sel.getRangeAt(0).cloneRange();
    });
    linkBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      linkDialog.style.display = 'flex';
      linkUrlInput.value = '';
      setTimeout(() => linkUrlInput.focus(), 0);
    });
    toolbar.appendChild(linkBtn);

    // Contenteditable editor
    const richEditor = document.createElement('div');
    richEditor.contentEditable = 'true';
    richEditor.className = 'iw-content-editor';
    richEditor.addEventListener('click', e => e.stopPropagation());
    this._iwText = richEditor;

    // Link dialog (inline below editor)
    const linkDialog = document.createElement('div');
    linkDialog.className = 'iw-link-dialog';
    linkDialog.style.display = 'none';

    const linkUrlInput = document.createElement('input');
    linkUrlInput.type = 'url';
    linkUrlInput.className = 'iw-link-input';
    linkUrlInput.placeholder = 'https://...';
    linkUrlInput.addEventListener('click', e => e.stopPropagation());

    const applyLink = () => {
      const url = linkUrlInput.value.trim();
      linkDialog.style.display = 'none';
      if (!url) { richEditor.focus(); return; }
      richEditor.focus();
      if (_savedRange) {
        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(_savedRange);
        _savedRange = null;
      }
      document.execCommand('createLink', false, url);
      // Ensure links open in new tab
      richEditor.querySelectorAll('a[href]').forEach(a => { if (!a.target) a.target = '_blank'; });
    };

    linkUrlInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter')  { e.preventDefault(); applyLink(); }
      if (e.key === 'Escape') { e.preventDefault(); linkDialog.style.display = 'none'; richEditor.focus(); }
    });

    const linkApplyBtn = document.createElement('button');
    linkApplyBtn.type = 'button';
    linkApplyBtn.textContent = 'Apply';
    linkApplyBtn.className = 'iw-link-apply';
    linkApplyBtn.addEventListener('mousedown', e => e.preventDefault());
    linkApplyBtn.addEventListener('click', (e) => { e.stopPropagation(); applyLink(); });

    const linkCancelBtn = document.createElement('button');
    linkCancelBtn.type = 'button';
    linkCancelBtn.textContent = 'Cancel';
    linkCancelBtn.className = 'iw-link-cancel';
    linkCancelBtn.addEventListener('mousedown', e => e.preventDefault());
    linkCancelBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      linkDialog.style.display = 'none';
      richEditor.focus();
    });

    linkDialog.append(linkUrlInput, linkApplyBtn, linkCancelBtn);
    textWrap.append(toolbar, richEditor, linkDialog);
    textRow.append(textLabel, textWrap);
    fields.appendChild(textRow);

    // ── Font controls for Infowindow ──────────────────────────────
    const iwControls = document.createElement('div');
    iwControls.className = 'iw-editor-controls';

    // Font family
    const fontRow = document.createElement('div');
    fontRow.className = 'iw-editor-row';
    const fontLabel = document.createElement('label');
    fontLabel.textContent = 'Font';
    const fontSel = document.createElement('select');
    fontSel.className = 'iw-select';
    FONTS.forEach(f => {
      const opt = document.createElement('option');
      opt.value = f.value;
      opt.textContent = f.label;
      opt.style.fontFamily = `"${f.label}", sans-serif`;
      fontSel.appendChild(opt);
    });
    fontSel.addEventListener('change', () => {
      loadFont(fontSel.value);
    });
    fontRow.append(fontLabel, fontSel);

    // Font size
    const iwSizeRow = document.createElement('div');
    iwSizeRow.className = 'iw-editor-row';
    const iwSizeLabel = document.createElement('label');
    iwSizeLabel.textContent = 'Size';
    const sizeInput = document.createElement('input');
    sizeInput.type = 'number';
    sizeInput.min = 8;
    sizeInput.max = 72;
    sizeInput.value = 14;
    sizeInput.className = 'iw-size-input';
    iwSizeRow.append(iwSizeLabel, sizeInput);

    // Text alignment
    const alignRow = document.createElement('div');
    alignRow.className = 'iw-editor-row';
    const alignLabel = document.createElement('label');
    alignLabel.textContent = 'Align';
    const alignWrap = document.createElement('div');
    alignWrap.className = 'iw-align-wrap';

    this._iwAlignBtns = {};
    for (const [val, label] of [['left', 'L'], ['center', 'C'], ['right', 'R']]) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.textContent = label;
      btn.className = 'iw-align-btn';
      btn.title = `Align ${val}`;
      btn.addEventListener('click', () => {
        Object.values(this._iwAlignBtns).forEach(b => b.classList.remove('iw-align-active'));
        btn.classList.add('iw-align-active');
        this._iwAlign = val;
      });
      alignWrap.appendChild(btn);
      this._iwAlignBtns[val] = btn;
    }
    this._iwAlign = 'left';
    this._iwAlignBtns['left'].classList.add('iw-align-active');
    alignRow.append(alignLabel, alignWrap);

    iwControls.append(fontRow, iwSizeRow, alignRow);
    this._iwFontSel = fontSel;
    this._iwSizeInput = sizeInput;

    const btns = document.createElement('div');
    btns.className = 'iw-editor-btns';

    const enterBtn = document.createElement('button');
    enterBtn.textContent = 'enter';
    enterBtn.className = 'iw-btn-enter';
    enterBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      if (!this._activeEl) return;
      this._activeEl.dataset.iwTitle   = this._iwTitle.value;
      this._activeEl.dataset.iwSubhead = this._iwSubhead.value;
      this._activeEl.dataset.iwText    = this._iwText.innerHTML;
      this._activeEl.dataset.iwFont    = this._iwFontSel.value;
      this._activeEl.dataset.iwSize    = this._iwSizeInput.value;
      this._activeEl.dataset.iwAlign   = this._iwAlign;
    });

    const clearBtn = document.createElement('button');
    clearBtn.textContent = 'clear';
    clearBtn.className = 'iw-btn-clear';
    clearBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      if (!this._activeEl) return;
      delete this._activeEl.dataset.iwTitle;
      delete this._activeEl.dataset.iwSubhead;
      delete this._activeEl.dataset.iwText;
      delete this._activeEl.dataset.iwFont;
      delete this._activeEl.dataset.iwSize;
      delete this._activeEl.dataset.iwAlign;

      this._iwTitle.value   = '';
      this._iwSubhead.value = '';
      this._iwText.innerHTML = '';
      this._iwFontSel.value = 'Inter';
      this._iwSizeInput.value = 14;
      this._iwAlign = 'left';
      Object.values(this._iwAlignBtns).forEach(b => b.classList.remove('iw-align-active'));
      this._iwAlignBtns['left'].classList.add('iw-align-active');
    });

    btns.append(enterBtn, clearBtn);
    this._iwPanel.appendChild(fields);
    this._iwPanel.appendChild(iwControls);
    this._iwPanel.appendChild(btns);
    document.body.appendChild(this._iwPanel);

    // Deselect on map click outside an icon
    this._map.on('click', (e) => {
      if (!e.originalEvent.target.closest?.('.map-icon')) this._deselectIcon();
    });

  }

  _addIcon(iconName) {
    const center = this._map.getCenter();
    const el = document.createElement('div');
    el.className = 'map-icon';
    el.dataset.icon = iconName;
    el.style.width  = `${this._iconSize}px`;
    el.style.height = `${this._iconSize}px`;
    el.dataset.size = this._iconSize;

    const img = document.createElement('img');
    img.src = `/icons/${iconName}-${ICON_SIZE}.svg`;
    img.style.width  = `${this._iconSize}px`;
    img.style.height = `${this._iconSize}px`;
    el.appendChild(img);

    el.addEventListener('click', (e) => {
      e.stopPropagation();
      this._selectIcon(el);
    });

    const del = document.createElement('button');
    del.className = 'mi-del';
    del.type = 'button';
    del.textContent = '✕';
    del.addEventListener('click', (e) => {
      e.stopPropagation();
      if (this._activeEl === el) this._deselectIcon();
      marker.remove();
      this._icons = this._icons.filter(i => i.marker !== marker);
    });
    el.appendChild(del);

    const marker = new maplibregl.Marker({
      element: el,
      draggable: true,
      anchor: 'center',
    })
    .setLngLat(center)
    .addTo(this._map);

    // Store lng/lat for export
    el.setAttribute('data-lng', center.lng.toString());
    el.setAttribute('data-lat', center.lat.toString());

    // Let MapLibre's native Marker positioning handle all move/zoom updates.
    // dragend only needs to update the stored data attributes for export.
    marker.on('dragend', () => {
      const lngLat = marker.getLngLat();
      el.setAttribute('data-lng', lngLat.lng.toString());
      el.setAttribute('data-lat', lngLat.lat.toString());
    });

    this._icons.push({ marker, el, iconName });
  }

  /** Hide all icon markers during zoom to prevent pipeline-mismatch drift. */
  hideAll() {
    this._icons.forEach(({ el }) => { el.style.visibility = 'hidden'; });
  }

  /**
   * Reveal all icon markers after a zoom animation fully settles.
   * By the time this is called, MapLibre's own moveend → _update() has already
   * projected every marker to the correct pixel position — we just unhide.
   */
  showAll() {
    this._icons.forEach(({ el }) => { el.style.visibility = ''; });
  }


  _selectIcon(el) {
    if (this._activeEl === el) return;
    this._deselectIcon();
    this._activeEl = el;
    el.classList.add('map-icon-selected');
    // Sync slider to this icon's current size
    const size = parseInt(el.dataset.size) || ICON_SIZE;
    this._iconSize = size;
    if (this._sizeSlider) {
      this._sizeSlider.value   = size;
      this._sizeValEl.textContent = `${size}px`;
    }

    // Populate and show Infowindow editor
    this._iwTitle.value     = el.dataset.iwTitle   || '';
    this._iwSubhead.value   = el.dataset.iwSubhead || '';
    this._iwText.innerHTML  = el.dataset.iwText    || '';
    this._iwFontSel.value = el.dataset.iwFont    || 'Inter';
    this._iwSizeInput.value = el.dataset.iwSize  || 14;
    this._iwAlign = el.dataset.iwAlign || 'left';
    Object.values(this._iwAlignBtns).forEach(b => b.classList.remove('iw-align-active'));
    if (this._iwAlignBtns[this._iwAlign]) {
      this._iwAlignBtns[this._iwAlign].classList.add('iw-align-active');
    }
    this._iwPanel.style.display = 'block';
  }

  _deselectIcon() {
    if (this._activeEl) {
      this._activeEl.classList.remove('map-icon-selected');
      this._activeEl = null;
    }
    if (this._iwPanel) this._iwPanel.style.display = 'none';
  }

  _resizeIcon(el, size) {
    el.style.width  = `${size}px`;
    el.style.height = `${size}px`;
    el.dataset.size = size;
    const img = el.querySelector('img');
    if (img) { img.style.width = `${size}px`; img.style.height = `${size}px`; }
    // Re-set the marker's lngLat so MapLibre recalculates the anchor offset
    // immediately to account for the new element dimensions.
    const entry = this._icons.find(i => i.el === el);
    if (entry) entry.marker.setLngLat(entry.marker.getLngLat());
  }

}

